import { apiRoot } from "./apiRoot";
import { Language } from "../../components/TranslatableEditor/translations_definitions";
import { TranslationsStorage } from "../../components/TranslatableEditor/TranslatableEditor.d";
import { JamezzLanguage } from "../../variables/app";
import { UpsellTriggersData } from "../../views/Upsell/Forms/VariantUpsell/UpsellAddTriggersStep";

const upsellEndpoints = apiRoot.enhanceEndpoints({
  addTagTypes: ["Upsell"],
});

export type MysqlID = number;
/**
 * A model that may or may not be saved to the server.
 */
export type ModelBud<T> = Omit<T, "id"> & {
  id: MysqlID | undefined;
};

export type DisplayableProduct = Pick<ProductModel, "id" | "name" | "system_name" | "active" | "blocked" | "price">;
export type DisplayableMenu = Pick<MenuModel, "id" | "naam">;

export type UpsellVariantType = "upsell" | "upscale" | "one_time_upsell" | "no_food_upsell" | "no_drinks_upsell";
type UpsellPageVariant = "suggestion";
export type UpsellPage = {
  id: MysqlID;
  name: TranslationsStorage;
  description: TranslationsStorage;
  products: DisplayableProduct[];
  upsell_page_variant: UpsellPageVariant;
};

export type OpeningHoursDay = "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday";
export type UpsellFlow = {
  id: number;
  pages: UpsellPage[];
  time_schedule: {
    id: number;
    name: string;
    opening_hours: Record<OpeningHoursDay, string[]>;
    is_active: boolean;
    valid_from: string;
    valid_until: string | null;
  };
};
export type UpsellPagesData = {
  flows: UpsellFlow[];
};

export type UpsellTriggers = {
  trigger_products: ProductModel[];
  trigger_menus: MenuModel[];
};

export type UpsellForm = {
  id: number;
  name: string;
  activated: boolean;
  upsell_variant: UpsellVariantType;
};

export type ProductModel = {
  id: number;
  name: string;
  system_name: string;
  price: number;
  active: boolean;
  blocked: boolean;
};

export type MenuModel = {
  id: number;
  naam: string;
};

export type Upsell = {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  upsell_variant: UpsellVariantType;
  activated: boolean;
};

export type Salesarea = {
  id: number;
  naam: string;
  menuSalesareaId: number;
  extra_settings: {
    showLangselLanguages: Language[];
  };
  mainLanguage: JamezzLanguage;
};

export type Paginated<D> = {
  data: D[];

  links: {
    first: string;
    last: string;
    next: null | string;
    prev: null | string;
  };

  meta: {
    links: {
      label: string;
      active: boolean;
      url: null | string;
    }[];

    // Pages
    current_page: number;
    last_page: number;
    from: number;
    to: number;

    // Config
    per_page: number;
    total: number;
  };
};

export const upsellApi = upsellEndpoints.injectEndpoints({
  endpoints: (builder) => ({
    fetchSalesarea: builder.query<{ data: Salesarea }, number>({
      query: (id) => {
        return {
          url: `/salesareas/${id}`,
          method: "GET",
        };
      },
    }),
    fetchUpsells: builder.query<
      Paginated<Upsell & UpsellTriggers & UpsellPagesData>,
      {
        salesareaId: number;
        pagination: {
          page: number;
          perPage: number;
        };
      }
    >({
      query: ({ salesareaId, pagination: { page, perPage } }) => {
        return {
          url: `/upsells?filter[salesarea_id][]=${salesareaId}&per_page=${perPage}&page=${page}`,
          method: "GET",
        };
      },
      providesTags: (result, error, page) =>
        result
          ? [...result.data.map(({ id }) => ({ type: "Upsell" as const, id })), { type: "Upsell", id: "PARTIAL-LIST" }]
          : [{ type: "Upsell", id: "PARTIAL-LIST" }],
    }),
    fetchUpsell: builder.query<{ data: Upsell & UpsellTriggers & UpsellPagesData }, { upsellId: UpsellForm["id"] }>({
      query({ upsellId }) {
        return {
          url: `/upsells/${upsellId}`,
          method: "GET",
        };
      },
    }),
    toggleUpsellActive: builder.mutation<any, { id: number }>({
      invalidatesTags: ({ id }) => [{ type: "Upsell", id }],
      query: ({ id }) => ({
        url: `/upsells/${id}/toggleActive`,
        method: "POST",
      }),
    }),
    deleteUpsell: builder.mutation<any, { id: number }>({
      invalidatesTags: () => ["Upsell"],
      query: ({ id }) => {
        return {
          url: `/upsells/${id}`,
          method: "DELETE",
        };
      },
    }),
    deleteUpsellFlow: builder.mutation<unknown, number>({
      invalidatesTags: () => ["Upsell"],
      query: (id) => {
        return {
          url: `/upsells/flows/${id}`,
          method: "DELETE",
        };
      },
    }),
    createUpsell: builder.mutation<
      any,
      {
        upsell: UpsellForm;
        salesarea_id: number;
      }
    >({
      invalidatesTags: ["Upsell"],
      query: (body) => {
        return {
          url: `/upsells`,
          method: "POST",
          body,
        };
      },
    }),
    reorderUpsellFlowPages: builder.mutation<unknown, { upsellFlowId: number; order: number[] }>({
      query: ({ upsellFlowId, order }) => {
        return {
          url: `/upsells/flows/${upsellFlowId}/reorderPages`,
          method: "PATCH",
          body: { order },
        };
      },
    }),
    updateUpsellFlow: builder.mutation<unknown, UpsellFlow>({
      query: ({ id, ...rest }) => {
        return {
          url: `upsells/flows/${id}`,
          method: "PATCH",
          body: {
            id,
            ...rest,
          },
        };
      },
    }),
    deletePage: builder.mutation<unknown, { pageId: MysqlID }>({
      query: ({ pageId }) => {
        return {
          url: `/upsells/pages/${pageId}`,
          method: "DELETE",
        };
      },
    }),
    modifyUpsellPageProducts: builder.mutation<unknown, { pageId: number; product_ids: number[] }>({
      query: ({ pageId, product_ids }) => {
        return {
          url: `/upsells/pages/${pageId}/modifyProducts`,
          method: "PATCH",
          body: {
            product_ids,
          },
        };
      },
    }),
    modifyPageTexts: builder.mutation<
      unknown,
      { pageId: number; texts: Record<"name" | "description", TranslationsStorage> }
    >({
      query: ({ pageId, texts }) => {
        return {
          url: `/upsells/pages/${pageId}/modifyTexts`,
          method: "PATCH",
          body: {
            texts,
          },
        };
      },
    }),
    updateUpsellTriggers: builder.mutation<unknown, { upsell_id: MysqlID; triggers: UpsellTriggersData["triggers"] }>({
      query: ({ upsell_id, triggers }) => {
        return {
          url: `/upsells/${upsell_id}/triggers`,
          method: "PATCH",
          body: {
            triggers,
          },
        };
      },
    }),
  }),
});

export const {
  // TODO Laura: Move
  useFetchSalesareaQuery,

  // upsell
  useCreateUpsellMutation,
  useFetchUpsellsQuery,
  useFetchUpsellQuery,
  useDeleteUpsellMutation,
  useToggleUpsellActiveMutation,
  useUpdateUpsellTriggersMutation,

  // upsell flow
  useUpdateUpsellFlowMutation,
  useDeleteUpsellFlowMutation,
  useReorderUpsellFlowPagesMutation,

  // upsell pages
  useModifyUpsellPageProductsMutation,
  useModifyPageTextsMutation,
  useDeletePageMutation,
} = upsellApi;
